/**
 * useSignatureStudioAgentTools — wires the Signature Studio into FutureBot.
 *
 * Tool implementations execute in the browser against the page's studioApi
 * (read through a ref so stable closures always see fresh state); every
 * mutation goes through the page's applyMutation so undo history, autosave,
 * and the glow highlight all fire exactly like a manual edit. Tool names
 * must match functions/FutureBot/toolsets.js (SIGNATURE_STUDIO_TOOLS).
 *
 * Install/copy stay human-only buttons — the bot designs, the user ships.
 */

import { useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import {
  addBlock,
  addRow,
  moveBlock,
  removeBlock,
  removeRow,
  renameSignature,
  updateBlock,
  updateColumn,
  updateGlobalStyle,
} from '../SignatureStudio/signatureSpec';
import { importSignatureHtml } from '../SignatureStudio/importSignatureHtml';

const STUDIO_SUGGESTIONS = [
  'Import my current Gmail signature so we can edit it',
  'Build me a professional signature from my profile',
  'Make it match our brand colors',
  'Add an Apply Now button that links to my website',
];

function shapeSpecForModel(spec) {
  if (!spec) return null;
  return {
    name: spec.name,
    global: spec.global,
    rows: spec.rows.map((row) => ({
      id: row.id,
      spacingTop: row.spacingTop || 0,
      columns: row.columns.map((col) => ({
        id: col.id,
        width: col.width || null,
        valign: col.valign,
        align: col.align,
        paddingLeft: col.paddingLeft || 0,
        paddingRight: col.paddingRight || 0,
        borderLeft: col.borderLeft || null,
        blocks: col.blocks,
      })),
    })),
  };
}

function shapeImages(images) {
  return (images || []).map((img) => ({
    name: img.name,
    url: img.url,
    width: img.width,
    height: img.height,
  }));
}

export function useSignatureStudioAgentTools(studioApiRef, { enabled = true } = {}) {
  const api = useCallback(() => studioApiRef.current, [studioApiRef]);

  const tools = useMemo(() => ({
    get_signature_state: () => {
      const spec = api().getSpec();
      if (!spec) return { error: 'The signature has not loaded yet.' };
      const stats = api().getStats();
      return {
        spec: shapeSpecForModel(spec),
        stats: { chars: stats.chars, limit: stats.limit, overLimit: stats.overLimit, imageCount: stats.imageCount },
        uploadedImages: shapeImages(api().getImages()),
        canUndo: api().canUndo(),
      };
    },

    apply_template: (args) => {
      const result = api().applyTemplate(args?.templateId);
      if (!result.ok) return { error: result.error };
      const stats = api().getStats();
      // Return the fresh spec inline so the model gets the new block ids
      // without spending another round on get_signature_state.
      return {
        ok: true,
        note: 'Template applied using the user\'s profile data. The updated spec (with new ids) is below — no need to re-fetch state.',
        spec: shapeSpecForModel(api().getSpec()),
        stats: { chars: stats.chars, overLimit: stats.overLimit },
      };
    },

    replace_signature: (args) => {
      const result = api().replaceSpec(args?.spec);
      if (!result.ok) return { error: result.error };
      const stats = api().getStats();
      // Sanitization can regenerate missing/duplicate ids, so echo the
      // canonical spec back — the model must use these ids for follow-up edits.
      return {
        ok: true,
        spec: shapeSpecForModel(api().getSpec()),
        stats: { chars: stats.chars, overLimit: stats.overLimit },
      };
    },

    update_global_style: (args) => {
      const result = api().applyMutation(updateGlobalStyle(api().getSpec(), args || {}), []);
      return result.ok ? { ok: true } : { error: result.error };
    },

    rename_signature: (args) => {
      const result = api().applyMutation(renameSignature(api().getSpec(), args?.name), []);
      return result.ok ? { ok: true } : { error: result.error };
    },

    add_row: (args) => {
      const result = api().applyMutation(addRow(api().getSpec(), args || {}));
      return result.ok ? { ok: true, rowId: result.rowId, columnIds: result.columnIds } : { error: result.error };
    },

    remove_row: (args) => {
      const result = api().applyMutation(removeRow(api().getSpec(), args?.rowId), []);
      return result.ok ? { ok: true } : { error: result.error };
    },

    update_column: (args) => {
      const { columnId, ...patch } = args || {};
      const result = api().applyMutation(updateColumn(api().getSpec(), columnId, patch), []);
      return result.ok ? { ok: true } : { error: result.error };
    },

    add_block: (args) => {
      const result = api().applyMutation(addBlock(api().getSpec(), {
        columnId: args?.columnId,
        block: args?.block,
        position: args?.position,
      }));
      return result.ok ? { ok: true, blockId: result.blockId } : { error: result.error };
    },

    update_block: (args) => {
      const result = api().applyMutation(
        updateBlock(api().getSpec(), args?.blockId, args?.patch || {}),
        [args?.blockId]
      );
      return result.ok ? { ok: true } : { error: result.error };
    },

    remove_block: (args) => {
      const result = api().applyMutation(removeBlock(api().getSpec(), args?.blockId), []);
      return result.ok ? { ok: true } : { error: result.error };
    },

    move_block: (args) => {
      const result = api().applyMutation(
        moveBlock(api().getSpec(), {
          blockId: args?.blockId,
          toColumnId: args?.toColumnId,
          position: args?.position,
        }),
        [args?.blockId]
      );
      return result.ok ? { ok: true } : { error: result.error };
    },

    import_current_signature: async () => {
      let html = '';
      try {
        const functions = getFunctions(app, 'us-east1');
        const fetchSignature = httpsCallable(functions, 'fetchUserSignature');
        const result = await fetchSignature({});
        html = result.data?.signature || '';
      } catch (err) {
        return { error: `Could not read the Gmail signature: ${err.message}` };
      }
      if (!html.trim()) {
        return { error: 'The user has no signature set in Gmail. Offer to build one from a template instead.' };
      }
      const imported = await importSignatureHtml(html);
      if (!imported.ok) return { error: imported.error };
      const applied = api().importAsNewSignature(imported.spec, imported.warnings);
      if (!applied.ok) return { error: applied.error };
      const stats = api().getStats();
      return {
        ok: true,
        note: 'Imported the user\'s current Gmail signature as a NEW signature (their other drafts are untouched). The reconstructed spec is below — use these ids for edits, no need to re-fetch state.',
        warnings: imported.warnings,
        spec: shapeSpecForModel(api().getSpec()),
        stats: { chars: stats.chars, overLimit: stats.overLimit },
      };
    },

    list_images: () => ({
      uploadedImages: shapeImages(api().getImages()),
      note: 'Only these hosted images can be used in the signature. Ask the user to upload via the Images tab if something is missing — you cannot upload files.',
    }),

    undo_last_change: () => {
      const undone = api().undo();
      return undone ? { ok: true } : { error: 'Nothing to undo.' };
    },
  }), [api]);

  const getContext = useCallback(async () => {
    const spec = api().getSpec();
    const stats = api().getStats();
    const images = api().getImages();
    const profile = api().getProfile();
    const blockCount = spec
      ? spec.rows.reduce((n, r) => n + r.columns.reduce((m, c) => m + c.blocks.length, 0), 0)
      : 0;
    return {
      referenceData: {
        userProfile: profile,
        uploadedImages: shapeImages(images),
      },
      stateSummary: spec
        ? `Editing signature "${spec.name}" — ${spec.rows.length} row(s), ${blockCount} block(s), ${stats.chars}/${stats.limit} chars${stats.overLimit ? ' (OVER THE GMAIL LIMIT)' : ''}. ${images.length} uploaded image(s) available.`
        : 'Signature still loading.',
    };
  }, [api]);

  const registration = useMemo(() => {
    if (!enabled) return null;
    return {
      pageId: 'crm-signature-studio',
      title: 'Signature Studio',
      tools,
      getContext,
      suggestions: STUDIO_SUGGESTIONS,
    };
  }, [enabled, tools, getContext]);

  return { registration };
}

export default useSignatureStudioAgentTools;
